import { z } from 'zod';

import { EmailStringSchema, HexColorSchema } from '../../common';
import { makeEndpoint } from '../common';

export enum THEME {
  DEFAULT = 'default',
  DARK = 'dark',
}

export enum NOTIFICATION_PLATFORM {
  EMAIL = 'email',
  SMS = 'sms',
}

export enum NOTIFICATION_TYPE {
  BOOKING_CONFIRMATION = 'booking:confirmation',
  BOOKING_MODIFIED = 'booking:modified',
  BOOKING_REFUNDED = 'booking:refunded',
  BOOKING_CANCELLED = 'booking:cancelled',
  BOOKING_RESCHEDULED = 'booking:rescheduled',
  REVIEW_REQUEST = 'review:request',
  REVIEW_REMINDER = 'review:reminder',
  REVIEW_RECEIVED = 'review:received',
  EVENT_REMINDER = 'event:reminder',
  EVENT_SECOND_REMINDER = 'event:second-reminder',
  EXPERIENCE_APPROVED = 'experience:approved',
  EXPERIENCE_UNPUBLISHED = 'experience:unpublished',
  EXPERIENCE_DELETED = 'experience:deleted',
  EXPERIENCE_ASSIGNED = 'experience:assigned',
  EXPERIENCE_UNASSIGNED = 'experience:unassigned',
  AVAILABILITY_REMINDER = 'experience:availability-reminder',
  AUCTION_CANCELED = 'auction:canceled',
  AUCTION_CLOSING_SOON = 'auction:closing-soon',
  AUCTION_CLOSED = 'auction:closed',
  AUCTION_WON = 'auction:won',
  AUCTION_OUT_BID = 'auction:out-bid',
  AUCTION_HIGHEST_BID = 'auction:highest-bid',
  GUEST_REQUEST_TO_BOOK_CREATED = 'experience:guest-request-to-book-created',
}

export enum StripePaymentMethods {
  CARD = 'card',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
  AMAZON_PAY = 'amazon_pay',
  CASH_APP_PAY = 'cashapp',
  LINK = 'link',
  ALI_PAY = 'alipay',
  WECHAT_PAY = 'wechat_pay',
}

export const PaymentMethodSettingsSchema = z.object({
  [StripePaymentMethods.APPLE_PAY]: z.boolean().optional(),
  [StripePaymentMethods.GOOGLE_PAY]: z.boolean().optional(),
  [StripePaymentMethods.AMAZON_PAY]: z.boolean().optional(),
  [StripePaymentMethods.CASH_APP_PAY]: z.boolean().optional(),
  [StripePaymentMethods.LINK]: z.boolean().optional(),
  [StripePaymentMethods.ALI_PAY]: z.boolean().optional(),
  [StripePaymentMethods.WECHAT_PAY]: z.boolean().optional(),
});

export enum BRAND_EMAIL_ADDRESS_TYPE {
  FROM = 'from',
  BOOKINGS = 'bookings',
  HOST_SUBMISSIONS = 'hostSubmissions',
}

export enum AiCurationTier {
  FREE = 'free', // 3 generation per month
  TIER_1 = 'tier1', // 15 generations per month
  UNLIMITED = 'unlimited', // Unlimited generations
}

export const AI_CURATION_TIER_LIMITS: Record<AiCurationTier, number | null> = {
  [AiCurationTier.FREE]: 3,
  [AiCurationTier.TIER_1]: 15,
  [AiCurationTier.UNLIMITED]: null, // null means unlimited
};

export enum AVAILABLE_BRAND_FEATURES {
  ADOBE_ANALYTICS = 'integrations:adobeAnalytics',
  AI_ASSISTANT = 'aiAssistant',
  AI_CURATION = 'aiCuration',
  AI_CURATION_2_0 = 'aiCuration2.0',
  AI_EXTRACT = 'aiExtract',
  AI_HOLIDAY_CURATION = 'aiHolidayCuration',
  ALICE = 'integrations:alice',
  API_ACCESS = 'apiAccess',
  AUTO_UNPUBLISH_LISTINGS_WITHOUT_AVAILABILITY = 'autoUnpublishListingsWithoutAvailability',
  AUTOMATED_REPORTS = 'automatedReports',
  BACKEND_BOOKING = 'backendBooking',
  BYPASS_CTA_BLOCK = 'bypassCTABlock',
  CATALOG_RECOMMENDATIONS = 'catalogRecommendations',
  CHECK_IN = 'check-in',
  CONDITIONAL_PRICING = 'conditionalPricing',
  CREDIT_CARD = 'creditCard',
  CUSTOM_BOOKING_EMAIL_MESSAGE = 'customBookingEmailMessage',
  CUSTOM_QUESTIONS = 'customQuestions',
  DATA_PLUS = 'dataPlus',
  DISABLE_ACTIVATE_SCROLLING_ANIMATION = 'disableActivateScrollingAnimation',
  EMAIL_CUSTOMIZATION = 'emailCustomization',
  EMAIL_DNS_SETTINGS = 'emailDnsSettings',
  ENABLE_AI_IMAGE_ALT_TEXT = 'enableAiImageAltText',
  ENABLE_ALASKA_LMS = 'enableAlaskaLms',
  ENABLE_AUCTIONS = 'enableAuctions',
  ENABLE_AUCTIONS_AUTO_BOOKING = 'enableAuctionsAutoBooking',
  ENABLE_AUCTIONS_DEBUG = 'enableAuctionsDebug',
  ENABLE_AUTO_TRANSLATE = 'enableAutoTranslate',
  ENABLE_BOOKING_SURCHARGE = 'enableBookingSurcharge',
  ENABLE_COMPOUND_TAX_CALCULATION = 'enableCompoundTaxCalculation',
  ENABLE_CREDIT_CARD_FEE_ROUTING_TO_HOST = 'enableCreditCardFeeRoutingToHost',
  ENABLE_CREDIT_CODE_LOOKUP = 'enableCreditCodeLookup',
  ENABLE_DEFAULT_TAX_DISTRIBUTION_TO_HOST = 'enableDefaultTaxDistributionToHost',
  ENABLE_DURATIONS = 'enableDurations',
  ENABLE_HIDE_ADD_TO_CART_BUTTON = 'enableHideAddToCartButton',
  ENABLE_HIDE_PRODUCT_LIST_CONTAINER = 'enableHideProductListContainer',
  ENABLE_HYATT_LMS = 'enableHyattLms',
  ENABLE_LIMITED_TIME_ACTIVATE_EXPERIENCES = 'enableLimitedTimeActivateExperiences',
  ENABLE_LIMITED_TIME_HOST_EXPERIENCES = 'enableLimitedTimeHostExperiences',
  ENABLE_NEW_LANDING_PAGE = 'enableNewLandingPage',
  ENABLE_NEW_PMS_TOTALS_CALCULATION = 'enableNewPmsTotalsCalculation',
  ENABLE_OPERA_USD_TO_MXN_CHARGE_CONVERSION = 'enableOperaUsdToMxnChargeConversion',
  ENABLE_MATCHMAKING = 'enableMatchmaking',
  ENABLE_PARTNER_COMMISSIONS = 'enablePartnerCommissions',
  ENABLE_PARTNER_INVITATIONS = 'enablePartnerInvitations',
  ENABLE_PRICE_DIFFERENCE_RESCHEDULING = 'enablePriceDifferenceRescheduling',
  ENABLE_PROMOTER = 'enablePromoter',
  ENABLE_PROMOTER_MONTHLY_FEE = 'enablePromoterMonthlyFee',
  ENABLE_PROMOTER_TRANSACTION_FEE = 'enablePromoterTransactionFee',
  ENABLE_MULTI_DAY_BOOKINGS = 'enableMultiDayBookings',
  ENABLE_REQUEST_BOOKINGS = 'enableRequestBookings',
  ENABLE_RESCHEDULE_BOOKINGS = 'enableRescheduleBookings',
  ENABLE_SHOW_ADVERTISED_AMOUNT = 'enableShowAdvertisingAmount',
  ENABLE_SPA = 'enableSpa',
  ENABLE_TRANSLATIONS = 'enableTranslations',
  ENABLE_VIBES_V2 = 'enableVibesV2',
  ENABLE_WALLETS_PAYMENT = 'enableWalletsPayment',
  ENABLE_WAY_COVERS_STRIPE_CHARGE_FX_FEE = 'enableWayCoversStripeChargeFxFee',
  ENHANCED_HOST_COMMISSION = 'enhancedHostCommission',
  ENHANCED_NOTIFICATIONS = 'enhancedNotifications',
  EXCLUSIVE_EXPERIENCE = 'exclusiveExperiences',
  EXPERIENCE_COMMENTS = 'experienceComments',
  EXPERIENCE_LISTING_TYPE = 'experienceListingType',
  FLEXIBLE_DURATION = 'flexibleDuration',
  GA4_ANALYTICS = 'integrations:ga4Analytics',
  GTM_ANALYTICS = 'integrations:gtmAnalytics',
  GUEST_MICRO_SURVEY = 'guestMicroSurvey',
  HIDE_DUPLICATE_HOST_EXPERIENCE = 'hideDuplicateHostExperience',
  HIDE_FIND_HOST = 'hideFindHost',
  HIDE_HOST = 'hideHost',
  HIDE_HOST_BUILDER = 'hideHostBuilder',
  HIDE_INVITE_HOST = 'hideInviteHost',
  IN_APP_MESSAGING = 'inAppMessaging',
  INFOR = 'integrations:infor',
  KICC = 'kicc',
  LIMITED_LICENSE_BRAND = 'limitedLicenseBrand',
  LINK_OUT_RECOMMENDATIONS = 'linkOutRecommendations',
  MEWS = 'integrations:mews',
  NEW_SESSION_UI = 'newSessionUi',
  OPERA = 'integrations:opera',
  PAUSE_AVAILABILITY_REMINDER = 'pauseAvailabilityReminder',
  PAY_UPON_ARRIVAL = 'payUponArrival',
  PREVENT_CATEGORY_MANAGEMENT = 'preventCategoryManagement',
  PREVENT_PMS_LOOKUP = 'preventPmsLookup',
  PRICE_TIERS = 'priceTiers',
  PRICING_RECOMMENDATIONS = 'pricingRecommendations',
  DYNAMIC_PRICING = 'dynamicPricing',
  REMOVE_CASH_PAYMENT = 'removeCashPayment',
  RESOURCE_POOLS = 'resourcePools',
  REVIEWS = 'reviews',
  REVINATE = 'integrations:revinate',
  ROOM_MEMBER_CHARGE = 'roomMemberCharge',
  SEAT_CHART = 'seatChart',
  SHOPPING_CART = 'shoppingCart',
  SIMPHONY = 'integrations:simphony',
  SMS_NOTIFICATION = 'smsNotifications',
  STAYNTOUCH = 'integrations:stayntouch',
  STOREFRONT = 'storefront',
  TEALIUM_ANALYTICS = 'integrations:tealiumAnalytics',
  TICKET_SCANNING = 'ticketScanning',
  UNLIMITED_HOST = 'unlimitedHost',
  UNLISTED_EXPERIENCE = 'unlistedExperience',
}

const DesignElementSchema = z
  .object({
    logoId: z.string().uuid().nullable(),
    identityImageId: z.string().uuid().nullable(),
    primaryColor: HexColorSchema.nullable(),
    secondaryColor: HexColorSchema.nullable(),
    buttonColor: HexColorSchema.nullable(),
    primaryFont: z.string().nullable(),
    secondaryFont: z.string().nullable(),
    theme: z.nativeEnum(THEME),
  })
  .partial();

const TemplateElementSchema = z.object({
  hero: z
    .object({
      desktopImageId: z.string().uuid().nullable(),
      mobileImageId: z.string().uuid().nullable(),
      intro: z.string().max(150).nullable(),
      heading: z.string().max(150).nullable(),
      description: z.string().nullable(),
    })
    .partial(),
});

const EmailSettingSchema = z.object({
  booking: z.object({ confirmationMessage: z.string().nullable() }),
});

const TermsAndConditionsSchema = z.object({
  documents: z.array(z.object({ title: z.string(), mediaId: z.string().uuid() })),
});

const WhitelistedUrlSchema = z.string().refine((url) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'https:' || (parsedUrl.protocol === 'http:' && parsedUrl.hostname === 'localhost');
  } catch {
    return false;
  }
});

const WhitelistedDomainsSchema = z.array(WhitelistedUrlSchema);

const SeoSchema = z.object({
  sitemapBaseUrls: z.array(z.string()),
});

/** A top-level page of the storefront: a root catalog of its own, a tab after Browse and Schedule. */
const StorefrontPageSchema = z.object({
  catalogId: z.string().uuid(),
  /** Kept in the list but not shown to guests; the dashboard draws it with a shut eye. */
  hidden: z.boolean(),
});

/**
 * The storefront's home (`rootCatalogId`, the Browse tab; null until one is set) and its other
 * top-level pages, in the order their tabs appear. A page that is only `hidden` stays listed, so
 * no catalog is ever orphaned.
 */
const StorefrontSettingsSchema = z.object({
  rootCatalogId: z.string().uuid().nullable(),
  pages: z.array(StorefrontPageSchema).max(20).optional(),
});

export type StorefrontPage = z.infer<typeof StorefrontPageSchema>;
export type StorefrontSettings = z.infer<typeof StorefrontSettingsSchema>;

const FeaturesSchema = z.record(z.nativeEnum(AVAILABLE_BRAND_FEATURES), z.boolean());

const PromotionSchema = z.object({
  exclusiveTag: z.string(),
});

const DataPlusSettingsSchema = z.object({
  operatingAccountNumber: z.string().or(z.number()),
  propertyId: z.string().or(z.number()),
  advanceDepositAccountNumber: z.string().nullable(),
  emailAddress: z.string().nullable(),
});

const CustomStyleSchema = z.string();

const HostSignupSchema = z.object({
  headingText: z.string(),
  experienceOverview: z.string(),
  joinCommunityOverview: z.string(),
  hostFeaturedImageId: z.string().nullable(),
  experienceFeaturedImageId: z.string().nullable(),
  signupFeaturedImageId: z.string().nullable(),
});

export enum ControlCadenceBefore {
  HOUR_BEFORE = 'hours_before',
  WEEK_BEFORE = 'week_before',
}

export enum ControlCadenceAfter {
  HOUR_AFTER = 'hours_after',
  WEEK_AFTER = 'week_after',
}

export enum PayoutSchedule {
  TRANSACTION_DATE = 'transaction_date',
  EXPERIENCE_DATE = 'experience_date',
}

export const PaymentSettingSchema = z.object({
  payoutSchedule: z.nativeEnum(PayoutSchedule),
});
export type PaymentSetting = z.infer<typeof PaymentSettingSchema>;

export const BrandEmailNotificationSettingsSchema = z
  .object({
    [NOTIFICATION_TYPE.BOOKING_CONFIRMATION]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.BOOKING_REFUNDED]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.BOOKING_CANCELLED]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.BOOKING_RESCHEDULED]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.REVIEW_REQUEST]: z.object({
      enabled: z.boolean(),
      config: z.object({ value: z.number(), cadence: z.nativeEnum(ControlCadenceAfter) }).optional(),
    }),
    [NOTIFICATION_TYPE.REVIEW_REMINDER]: z.object({
      enabled: z.boolean(),
      config: z.object({ value: z.number(), cadence: z.nativeEnum(ControlCadenceAfter) }).optional(),
    }),
    [NOTIFICATION_TYPE.EVENT_REMINDER]: z.object({
      enabled: z.boolean(),
      config: z.object({ value: z.number(), cadence: z.nativeEnum(ControlCadenceBefore) }).optional(),
    }),
    [NOTIFICATION_TYPE.EVENT_SECOND_REMINDER]: z.object({
      enabled: z.boolean(),
      config: z.object({ value: z.number(), cadence: z.nativeEnum(ControlCadenceBefore) }).optional(),
    }),
    [NOTIFICATION_TYPE.BOOKING_MODIFIED]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.AUCTION_WON]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.AUCTION_OUT_BID]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.AUCTION_HIGHEST_BID]: z.object({ enabled: z.boolean() }),
    [NOTIFICATION_TYPE.GUEST_REQUEST_TO_BOOK_CREATED]: z.object({ enabled: z.boolean() }),
  })
  .partial();

export const BrandSmsNotificationSettingsSchema = BrandEmailNotificationSettingsSchema.omit({
  [NOTIFICATION_TYPE.REVIEW_REMINDER]: true,
  [NOTIFICATION_TYPE.REVIEW_REQUEST]: true,
  [NOTIFICATION_TYPE.BOOKING_MODIFIED]: true,
  [NOTIFICATION_TYPE.AUCTION_WON]: true,
  [NOTIFICATION_TYPE.AUCTION_OUT_BID]: true,
  [NOTIFICATION_TYPE.AUCTION_HIGHEST_BID]: true,
  [NOTIFICATION_TYPE.GUEST_REQUEST_TO_BOOK_CREATED]: true,
});

export const BrandNotificationSchema = z
  .object({
    [NOTIFICATION_PLATFORM.EMAIL]: BrandEmailNotificationSettingsSchema,
    [NOTIFICATION_PLATFORM.SMS]: BrandSmsNotificationSettingsSchema,
  })
  .partial();

const BrandEmailAddressesSchema = z
  .object({
    [BRAND_EMAIL_ADDRESS_TYPE.FROM]: EmailStringSchema.or(z.literal('')),
    [BRAND_EMAIL_ADDRESS_TYPE.BOOKINGS]: EmailStringSchema.or(z.literal('')),
    [BRAND_EMAIL_ADDRESS_TYPE.HOST_SUBMISSIONS]: EmailStringSchema.or(z.literal('')),
  } satisfies Record<BRAND_EMAIL_ADDRESS_TYPE, unknown>)
  .partial();

const ShouldValidateComplimentaryBookingSchema = z
  .object({
    roomCharge: z.boolean(),
    memberCharge: z.boolean(),
  })
  .partial();

const StripeSchema = z.record(z.unknown());

export const CalendarGroupingSchema = z.enum(['timeOfDay', 'exactTime', 'category']);
export type CalendarGrouping = z.infer<typeof CalendarGroupingSchema>;

/** Guest "Schedule View" (v3 calendar) configuration — stored on brand_settings.scheduleView. */
export const ScheduleViewSchema = z
  .object({
    showScheduleView: z.boolean(),
    showTabs: z.boolean(),
    rowImage: z.boolean(),
    rowDuration: z.boolean(),
    rowSpotsRemaining: z.boolean(),
    groupBy: CalendarGroupingSchema,
    chipsThreshold: z.number().int().min(1).max(20).nullable(),
    categoryFilter: z.boolean(),
    vibeFilter: z.boolean(),
    // Distance (px) from the top of the page where the sticky calendar header starts sticking —
    // lets a brand clear a fixed site header that overlaps the embed. `stickyTopOffset` is the
    // desktop value (retained under its original name so no DB migration is required);
    // `stickyTopOffsetMobile` is a separate per-viewport override capped lower because a
    // typical phone viewport is ~640-900px tall (anything beyond ~300px would push the sticky
    // header off-screen).
    stickyTopOffset: z.number().int().min(0).max(1000),
    stickyTopOffsetMobile: z.number().int().min(0).max(300),
  })
  .partial();

export const PutBrandSettingsRequestSchema = z.object({
  designElement: DesignElementSchema.optional(),
  templateElement: TemplateElementSchema.optional(),
  email: EmailSettingSchema.optional(),
  termsAndConditions: TermsAndConditionsSchema.optional(),
  whitelistedDomains: WhitelistedDomainsSchema.optional(),
  seo: SeoSchema.optional(),
  storefront: StorefrontSettingsSchema.optional(),
  features: FeaturesSchema.optional(),
  promotion: PromotionSchema.optional(),
  dataPlus: DataPlusSettingsSchema.optional(),
  customStyle: CustomStyleSchema.optional(),
  hostSignup: HostSignupSchema.optional(),
  notifications: BrandNotificationSchema.optional(),
  emailAddresses: BrandEmailAddressesSchema.optional(),
  shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional(),
  stripe: StripeSchema.optional(),
  payment: PaymentSettingSchema.optional(),
  paymentMethods: PaymentMethodSettingsSchema.optional(),
  cancellationPolicyId: z.string().uuid().nullable().optional(),
  reschedulePolicyId: z.string().uuid().nullable().optional(),
  scheduleView: ScheduleViewSchema.optional(),
});

export type PutBrandSettingsRequest = z.infer<typeof PutBrandSettingsRequestSchema>;

const DnsSettingsSchema = z.object({
  valid: z.boolean(),
  type: z.string(),
  host: z.string(),
  data: z.string(),
});
export const BrandDnsSettingsResponseSchema = z.object({
  mx: DnsSettingsSchema,
  spf: DnsSettingsSchema,
  dkim: DnsSettingsSchema,
});
export type BrandDnsSettingsResponse = z.infer<typeof BrandDnsSettingsResponseSchema>;

const DnsValidationSchema = z.object({
  valid: z.boolean(),
  reason: z.string().nullable(),
});
export const BrandDnsValidationResponseSchema = z.object({
  mx: DnsValidationSchema,
  spf: DnsValidationSchema,
  dkim: DnsValidationSchema,
});
export type BrandDnsValidationResponse = z.infer<typeof BrandDnsValidationResponseSchema>;

export const UpdateBrandSettingsEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/settings`,
  responseSchema: z.void(),
  requestSchema: PutBrandSettingsRequestSchema,
});

const UpdateBrandWebsiteUrlsRequestSchema = z.object({
  urls: WhitelistedDomainsSchema,
});

export const UpdateBrandWebsiteUrlsEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/website-urls`,
  responseSchema: z.void(),
  requestSchema: UpdateBrandWebsiteUrlsRequestSchema,
});

const UpdateBrandIdentityImageRequestSchema = z.object({
  searchQueryParam: z.string().nullable(),
});

export const UpdateBrandIdentityImageEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/settings/update-identity-image`,
  responseSchema: z.string().uuid(),
  requestSchema: UpdateBrandIdentityImageRequestSchema,
});
